import React, { useState, useEffect } from 'react';
import { XMarkIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import { updateFlowTransaction, deleteFlowTransaction } from 'views/finance/moduleServices/flow_requests';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';
import DropdownCustom from 'views/finance/moduleComponents/DropdownCustom';
import DropdownCurrency from 'views/finance/moduleComponents/DropdownCurrency';
import { useFinanceProfile } from '../../../moduleServices/finance_profilecontext';
import { getL0Options, getL1Options, getL2Options, buildL0Key, buildL1Key } from 'views/finance/moduleServices/helper_hierarchicalCategory';

const ModalFlowEdit = ({ selectedFlow, onClose, onSuccess, categories = [], hierarchicalCategories = {} }) => {
  const { currencyOptions } = useFinanceProfile();
  const [isEditing, setIsEditing] = useState(false);
  const [editedFlow, setEditedFlow] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset to read-only and set flow when opening
  useEffect(() => {
    if (selectedFlow) {
      const l0Key = buildL0Key(selectedFlow.categ_l0, selectedFlow.flow_type);
      const l1Key = buildL1Key(selectedFlow.categ_l0, selectedFlow.flow_type, selectedFlow.categ_l1);
      
      setEditedFlow({
        ...selectedFlow,
        categ_l0: { 
          value: l0Key,
          label: selectedFlow.categ_l0 
        },
        categ_l1: { 
          value: l1Key,
          label: selectedFlow.categ_l1 
        },
        categ_l2: { value: selectedFlow.categ_l2_id || selectedFlow.categ_l2, label: selectedFlow.categ_l2 },
        flow_account: selectedFlow.flow_account || '',
      });
      setIsEditing(false);
    }
  }, [selectedFlow]);

  if (!selectedFlow || !editedFlow) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Reset dependent categories when parent changes
    if (name === 'categ_l0') {
      setEditedFlow(prev => ({ 
        ...prev, 
        [name]: value,
        categ_l1: { value: '', label: '' },
        categ_l2: { value: '', label: '' }
      }));
    } else if (name === 'categ_l1') {
      setEditedFlow(prev => ({ 
        ...prev, 
        [name]: value,
        categ_l2: { value: '', label: '' }
      }));
    } else {
      setEditedFlow(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const l0Name = hierarchicalCategories.by_l0?.[editedFlow.categ_l0.value]?.name || '';
      const l1Name = hierarchicalCategories.by_l1?.[editedFlow.categ_l1.value]?.name || '';
      const l2Name = hierarchicalCategories.by_l2?.[editedFlow.categ_l2.value]?.name || '';
      const flowData = {
        ...editedFlow,
        categ_l0: l0Name,
        categ_l1: l1Name,
        categ_l2: l2Name,
        flow_value: parseFloat(editedFlow.flow_value),
        flow_account: editedFlow.flow_account || null
      };

      await updateFlowTransaction(selectedFlow.id, flowData);
      toast.success('Transaction updated successfully!');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update transaction');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this transaction? This action cannot be undone.')) {
      try {
        setSubmitting(true);
        await deleteFlowTransaction(selectedFlow.id);
        toast.success('Transaction deleted successfully!');
        onSuccess();
        onClose();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to delete transaction');
      } finally {
        setSubmitting(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-4 md:p-6 rounded-2xl shadow w-80 md:w-96 relative">
        {submitting && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="bg-white/90 rounded-xl shadow-lg p-4 border border-gray-200">
              <SpinnerLoad />
            </div>
          </div>
        )}
        <div className="absolute top-2 right-2 flex gap-2">
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              disabled={submitting}
              className={`text-gray-500 ${submitting ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-700'}`}
            >
              <PencilSquareIcon className="h-5 w-5" />
            </button>
          )}
          <button
            onClick={onClose}
            disabled={submitting}
            className={`text-gray-500 ${submitting ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-700'}`}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <h2 className="text-lg font-semibold mb-4">
          {isEditing ? 'Edit Transaction' : 'Transaction Details'}
        </h2>

        <form className="space-y-4" onSubmit={handleUpdate}>
          {/* Top Controls Row */}
          <div className="flex items-center gap-1 md:gap-1.5 flex-wrap min-w-0">
            {/* Flow Type Buttons */}
            <div className="bg-gray-100 rounded-full p-0.5 inline-flex flex-shrink-0">
              {['In', 'Out'].map((type, index) => (
                <button
                  key={type}
                  type="button"
                  disabled={!isEditing || submitting}
                  onClick={() => {
                    setEditedFlow((prev) => {
                      const newL0Key = prev.categ_l0.label 
                        ? buildL0Key(prev.categ_l0.label, type)
                        : '';
                      
                      return {
                        ...prev,
                        flow_type: type,
                        categ_l0: { value: newL0Key, label: prev.categ_l0.label },
                        categ_l1: { value: '', label: '' },
                        categ_l2: { value: '', label: '' }
                      };
                    });
                  }}
                  className={`
                    px-3 py-1 text-xs font-medium transition-all
                    ${index === 0 ? 'rounded-l-full' : 'rounded-r-full'}
                    ${editedFlow.flow_type === type
                      ? type === 'In'
                        ? 'text-white bg-green-400'
                        : 'text-white bg-red-400'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                    }
                    ${(!isEditing || submitting) ? 'opacity-60 cursor-not-allowed' : ''}
                  `}
                >
                  {type}
                </button>
              ))}
            </div>

            {/* Account Input */}
            <input
              type="text"
              name="flow_account"
              value={editedFlow.flow_account || ''}
              onChange={handleInputChange}
              placeholder="Account"
              className="min-w-[80px] max-w-[150px] flex-1 px-2 py-1.5 border rounded-md text-xs"
              disabled={!isEditing || submitting}
            />

            {/* Category L0 Select */}
            <DropdownCustom
              options={getL0Options(hierarchicalCategories, editedFlow.flow_type)}
              selectedValue={editedFlow.categ_l0}
              onSelect={option => setEditedFlow(prev => ({
                ...prev, 
                categ_l0: option,
                categ_l1: { value: '', label: '' },
                categ_l2: { value: '', label: '' }
              }))}
              placeholder="Select L0"
              width="w-24 md:w-32"
              disabled={!isEditing || submitting}
              buttonClassName="ml-0.5 flex-shrink-0"
            />
          </div>

          {/* Value Input */}
          <div className="relative mt-4 mb-6">
            <div className="flex items-center justify-end gap-2">
              <input
                type="number"
                name="flow_value"
                value={editedFlow.flow_value}
                onChange={handleInputChange}
                placeholder="0.00"
                step="0.01"
                min="0"
                className="max-w-[200px] md:max-w-[300px] text-center text-4xl font-semibold py-3 appearance-none focus:outline-none bg-transparent [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                style={{ 
                  WebkitAppearance: 'none',
                  MozAppearance: 'textfield'
                }}
                disabled={!isEditing || submitting}
                required
              />
              <div className="flex-shrink-0">
                <DropdownCurrency
                  selectedValue={editedFlow.flow_currency}
                  onSelect={(value) => setEditedFlow(prev => ({ ...prev, flow_currency: value }))}
                  disabled={!isEditing || submitting}
                  usePortal={false}
                  options={currencyOptions}
                />
              </div>
            </div>
            <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent opacity-30"></div>
          </div>

          {/* Description */}
          <textarea
            name="desc"
            value={editedFlow.desc}
            onChange={handleInputChange}
            placeholder="Description"
            rows={2}
            className="w-full px-2 py-1.5 border rounded-md text-sm"
            disabled={!isEditing || submitting}
            required
          />

          {/* Date and Time */}
          <div className="flex gap-2">
            <input
              type="date"
              name="date"
              value={editedFlow.date}
              onChange={handleInputChange}
              className="w-1/2 px-2 py-1.5 border rounded-md text-sm"
              disabled={!isEditing || submitting}
              required
            />
            <input
              type="time"
              name="hour"
              value={editedFlow.hour}
              onChange={handleInputChange}
              className="w-1/2 px-2 py-1.5 border rounded-md text-sm"
              disabled={!isEditing || submitting}
              required
            />
          </div>

          {/* Categories */}
          <div className="flex gap-2">
            <div className="flex-1">
              <DropdownCustom
                options={getL1Options(hierarchicalCategories, editedFlow.categ_l0.value)}
                selectedValue={editedFlow.categ_l1}
                onSelect={option => setEditedFlow(prev => ({
                  ...prev, 
                  categ_l1: option,
                  categ_l2: { value: '', label: '' }
                }))}
                placeholder="Select L1"
                disabled={!isEditing || submitting || !editedFlow.categ_l0}
              />
            </div>
            <div className="flex-1">
              <DropdownCustom
                options={getL2Options(hierarchicalCategories, editedFlow.categ_l1.value)}
                selectedValue={editedFlow.categ_l2}
                onSelect={option => setEditedFlow(prev => ({
                  ...prev, 
                  categ_l2: option
                }))}
                placeholder="Select L2"
                disabled={!isEditing || submitting || !editedFlow.categ_l1}
              />
            </div>
          </div>

          {/* Reference */}
          <input
            type="text"
            name="reference"
            value={editedFlow.reference}
            onChange={handleInputChange}
            placeholder="Reference"
            className="w-full px-2 py-1.5 border rounded-md text-sm"
            disabled={!isEditing || submitting}
          />

          {/* Action Buttons */}
          {isEditing && (
            <div className="flex gap-4">
              <button
                type="submit"
                disabled={submitting}
                className={`flex-1 h-10 text-white rounded-2xl ${submitting ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
              >
                {submitting ? 'Updating...' : 'Update'}
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={submitting}
                className={`flex-1 h-10 text-white rounded-2xl flex items-center justify-center gap-2 ${submitting ? 'bg-red-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'}`}
              >
                <TrashIcon className="h-5 w-5" />
                {submitting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default ModalFlowEdit;
